import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { Link, useLoaderData } from "@remix-run/react";
import Footer from "~/components/Footer";
import { posts, getPost } from "~/lib/blog-data";
import styles from "~/styles/post.module.css";

export function loader({ params }: LoaderFunctionArgs) {
  const post = getPost(params.slug!);
  if (!post) throw new Response("Not Found", { status: 404 });
  return { post };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.post) return [{ title: "Not Found" }];
  return [
    { title: `${data.post.title.replace("\n", " ")} — Prodigium Dynamics` },
    { name: "description", content: data.post.lead.slice(0, 160) },
  ];
};

export default function PostPage() {
  const { post } = useLoaderData<typeof loader>();

  return (
    <>
      <div className={styles.articleHeader}>
        <div className={styles.articleMeta}>
          <span className={styles.tag}>{post.category}</span>
          <span className={styles.sep}>·</span>
          <span>{post.date}</span>
          <span className={styles.sep}>·</span>
          <span>{post.readTime}</span>
        </div>
        <h1 className={styles.articleTitle}>
          {post.title.split("\n").map((line, i) => (
            <span key={i}>{line}<br /></span>
          ))}
        </h1>
        <p className={styles.articleLead}>{post.lead}</p>
      </div>

      <div className={styles.articleBody}>
        {post.sections.map((s, i) => {
          if (s.type === "h2") return <h2 key={i} className={styles.h2}>{s.content}</h2>;
          if (s.type === "quote") return (
            <div key={i} className={styles.pullQuote}>
              <p>{s.content}</p>
            </div>
          );
          // WARNING: dangerouslySetInnerHTML is safe here only because blog-data.ts
          // contains hardcoded, author-controlled content. If migrating to a CMS,
          // sanitize with DOMPurify before injecting.
          return <p key={i} dangerouslySetInnerHTML={{ __html: s.content }} />;
        })}
      </div>

      <div className="rule" />

      <div className={styles.articleCta}>
        <div>
          <h3 className={styles.ctaTitle}>Read without <span>resistance.</span></h3>
          <p className={styles.ctaSub}>Try PageBurn free — no installation required.</p>
        </div>
        <a href="https://pageburn.site" target="_blank" rel="noopener" className="btn-primary">
          Open PageBurn →
        </a>
      </div>

      <div className={styles.articleNav}>
        {post.prev ? (
          <Link to={`/blog/${post.prev.slug}`}>← {post.prev.label}</Link>
        ) : (
          <Link to="/blog">← All articles</Link>
        )}
        {post.next ? (
          <Link to={`/blog/${post.next.slug}`}>Next: {post.next.label} →</Link>
        ) : (
          <Link to="/blog">All articles →</Link>
        )}
      </div>

      <Footer />
    </>
  );
}
